/*
 * tc-nav-intent.js: hover intent for the desktop menu panels (contract K2).
 *
 * Spec: docs/specs/2026-10-07-nav-redesign-design.md, section 4, P1.1.
 * Plan: docs/specs/2026-10-07-nav-redesign-plan-P1-hover-intent-current-section.md.
 * GitHub: TravelerChoiceT/shopify-theme issue #558 (epic #557). #594 moved
 * OPEN_DELAY from 300ms to 180ms (the menu felt slow to open); CLOSE_DELAY stays.
 *
 * Pure timer logic, no DOM. sections/custom-header.liquid creates one controller
 * per top-level item that has a panel, all in one group, and calls it from its
 * own listeners:
 *   - a mouse that rests on the item for OPEN_DELAY (180ms) opens the panel;
 *   - a mouse that leaves the item AND its panel closes it CLOSE_DELAY (120ms)
 *     later, and coming back onto either cancels that close;
 *   - switching: while another panel of the same group is open or inside its
 *     close delay, a mouse entering this item closes that panel and opens this
 *     one at once, in the same task, so no frame shows neither;
 *   - keyboard, click and touch act at once (openNow, closeNow, toggleNow) and
 *     cancel whatever was pending.
 * The delays never depend on prefers-reduced-motion: that setting removes fades,
 * not the delay (spec, section 4, P1.1).
 *
 * Node: module.exports exposes the same API, so tests/nav/intent.test.js can run
 * it with node:test mock timers. Browser: window.TCNavIntent. ES5 only.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (!root.TCNavIntent) {
    root.TCNavIntent = api;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var OPEN_DELAY = 180;
  var CLOSE_DELAY = 120;
  var groups = {};

  // A set of controllers whose panels replace one another (one menu bar).
  function createGroup() {
    return { members: [] };
  }

  // The shared group for a name: every per-item script that asks for the same
  // name gets the same group.
  function group(name) {
    if (!groups[name]) {
      groups[name] = createGroup();
    }
    return groups[name];
  }

  // options: { open: fn, close: fn, isOpen: fn returning boolean,
  //            openDelay: ms (default 180), closeDelay: ms (default 120),
  //            group: a group from group() or createGroup() (optional) }
  function create(options) {
    var open = options.open;
    var close = options.close;
    var isOpen = options.isOpen;
    var openDelay = typeof options.openDelay === 'number' ? options.openDelay : OPEN_DELAY;
    var closeDelay = typeof options.closeDelay === 'number' ? options.closeDelay : CLOSE_DELAY;
    var members = options.group ? options.group.members : null;
    var openTimer = null;
    var closeTimer = null;
    var controller;

    function clearOpen() {
      if (openTimer !== null) {
        clearTimeout(openTimer);
        openTimer = null;
      }
    }
    function clearClose() {
      if (closeTimer !== null) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }
    function cancel() {
      clearOpen();
      clearClose();
    }
    // Closes, at once, every other member that is open or inside its close
    // delay, and returns true when there was one. A member that is merely
    // resting toward its own 180ms open is not active, but its pending open
    // is still cancelled here: left alone, it would fire later regardless of
    // this open, and two panels would end up open (reviewer finding,
    // Important 1, fix round 1). That cancel-only case does not count toward
    // the return value, so pointerEnter's own rest is unaffected.
    function closeActiveOthers() {
      var found = false;
      if (!members) {
        return found;
      }
      for (var i = 0; i < members.length; i++) {
        if (members[i] === controller) {
          continue;
        }
        if (members[i].isActive()) {
          members[i].closeNow();
          found = true;
        } else {
          members[i].cancel();
        }
      }
      return found;
    }

    controller = {
      // A mouse entered the item or its panel.
      pointerEnter: function () {
        clearClose();
        if (isOpen() || openTimer !== null) {
          return;
        }
        if (closeActiveOthers()) {
          open();
          return;
        }
        openTimer = setTimeout(function () {
          openTimer = null;
          open();
        }, openDelay);
      },
      // A mouse left the item and its panel.
      pointerLeave: function () {
        clearOpen();
        if (!isOpen() || closeTimer !== null) {
          return;
        }
        closeTimer = setTimeout(function () {
          closeTimer = null;
          close();
        }, closeDelay);
      },
      // Keyboard, click or touch: no delay. Opening closes every other active
      // group member first, the same as pointerEnter, so one panel open at a
      // time holds for every input, not only a resting mouse. A toggle that
      // closes touches nothing else: closing never needs to make room.
      openNow: function () {
        cancel();
        closeActiveOthers();
        open();
      },
      closeNow: function () {
        cancel();
        close();
      },
      toggleNow: function () {
        cancel();
        if (isOpen()) {
          close();
        } else {
          closeActiveOthers();
          open();
        }
      },
      // Drops a pending open or close and changes nothing else.
      cancel: cancel,
      // Open, or still inside the close delay.
      isActive: function () {
        return isOpen() || closeTimer !== null;
      },
      // 'open', 'close' or '' (for tests and debugging).
      pending: function () {
        if (openTimer !== null) {
          return 'open';
        }
        if (closeTimer !== null) {
          return 'close';
        }
        return '';
      }
    };
    if (members) {
      members.push(controller);
    }
    return controller;
  }

  // True when the pointer that just entered should get the hover delay: a mouse,
  // or (when the browser reports no pointer type) a device whose primary pointer
  // can hover finely. Touch and pen keep the instant behavior they have today.
  function isMouse(pointerType, fallbackFine) {
    if (pointerType === 'mouse') {
      return true;
    }
    if (pointerType) {
      return false;
    }
    return !!fallbackFine;
  }

  return {
    OPEN_DELAY: OPEN_DELAY,
    CLOSE_DELAY: CLOSE_DELAY,
    create: create,
    createGroup: createGroup,
    group: group,
    isMouse: isMouse
  };
});
